import React, { useState } from 'react';

const GeojsonUploader = ({ areas, currentAreaId }) => {
  const [selectedArea, setSelectedArea] = useState(currentAreaId);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importOptions, setImportOptions] = useState({
    create_missing: false,
    overwrite: false,
    match_threshold: 0.8
  });
  const [importResults, setImportResults] = useState(null);
  const [manualMatches, setManualMatches] = useState({});

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.geojson')) {
      setSelectedFile(file);
      setError('');
      setPreview(null);
      setImportResults(null);
    } else {
      setError('Please select a valid GeoJSON file');
      setSelectedFile(null);
    }
  };

  const previewFile = async () => {
    if (!selectedFile) {
      setError('Please select a GeoJSON file');
      return;
    }

    setLoading(true);
    setError('');
    setPreview(null);

    const formData = new FormData();
    formData.append('geojson_file', selectedFile);
    formData.append('area_id', selectedArea);

    try {
      const response = await fetch('/admin/geojson_imports/preview', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        setPreview(data);
        // Initialize manual matches with the best automatic matches
        const initialMatches = {};
        data.preview.forEach((item, idx) => {
          if (item.status === 'exact') {
            // For exact matches, use the exact match
            initialMatches[idx] = item.exact_match ? item.geojson_name : null;
          } else if (item.fuzzy_matches && item.fuzzy_matches.length > 0) {
            // For fuzzy matches, pre-select the best match
            initialMatches[idx] = item.fuzzy_matches[0].id;
          } else {
            initialMatches[idx] = 'new'; // Will create new location
          }
        });
        setManualMatches(initialMatches);
      } else {
        setError(data.error || 'Failed to preview file');
      }
    } catch (err) {
      setError('Error previewing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const performImport = async () => {
    if (!selectedFile || !preview) {
      setError('Please preview the file first');
      return;
    }

    if (!confirm('Are you sure you want to import these locations? This will update boundary data.')) {
      return;
    }

    setLoading(true);
    setError('');
    setImportResults(null);

    const formData = new FormData();
    formData.append('geojson_file', selectedFile);
    formData.append('area_id', selectedArea);
    formData.append('create_missing', importOptions.create_missing);
    formData.append('overwrite', importOptions.overwrite);
    formData.append('match_threshold', importOptions.match_threshold);
    formData.append('manual_matches', JSON.stringify(manualMatches));

    try {
      const response = await fetch('/admin/geojson_imports/import', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        setImportResults(data.results);
        alert(data.message || 'Import completed successfully');
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Error during import: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl">
      {/* Step 1: Select Area and File */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Step 1: Select Area and GeoJSON File</h2>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">Area/Market</label>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {areas.map(area => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">GeoJSON File</label>
            <input
              type="file"
              accept=".geojson"
              onChange={handleFileSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {selectedFile && (
              <p className="text-sm text-green-600 mt-1">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>

        <button
          onClick={previewFile}
          disabled={loading || !selectedFile}
          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
        >
          {loading ? 'Loading Preview...' : 'Preview File'}
        </button>
      </div>

      {/* Preview Results */}
      {preview && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Step 2: Preview & Import Options</h2>
          
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <h3 className="font-semibold mb-2">File: {preview.filename}</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-green-600 font-semibold">{preview.summary.exact_matches}</span> exact matches
              </div>
              <div>
                <span className="text-yellow-600 font-semibold">{preview.summary.fuzzy_matches}</span> fuzzy matches
              </div>
              <div>
                <span className="text-blue-600 font-semibold">{preview.summary.new_locations}</span> new locations
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Total features in file: {preview.total_features}
            </div>
          </div>

          <div className="mb-4">
            <h3 className="font-semibold mb-2">Import Options</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={importOptions.create_missing}
                  onChange={(e) => setImportOptions({...importOptions, create_missing: e.target.checked})}
                  className="mr-2"
                />
                <span>Create new locations for unmatched items</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={importOptions.overwrite}
                  onChange={(e) => setImportOptions({...importOptions, overwrite: e.target.checked})}
                  className="mr-2"
                />
                <span>Overwrite existing boundaries</span>
              </label>
              
              <div className="flex items-center">
                <label className="mr-2">Match threshold:</label>
                <input
                  type="number"
                  min="0.5"
                  max="1"
                  step="0.1"
                  value={importOptions.match_threshold}
                  onChange={(e) => setImportOptions({...importOptions, match_threshold: parseFloat(e.target.value)})}
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-600">(0.5 = loose, 1.0 = exact)</span>
              </div>
            </div>
          </div>

          <div className="mb-4 max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">GeoJSON Name</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Auto Match</th>
                  <th className="px-4 py-2 text-left">Select Location</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((item, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="px-4 py-2 font-medium">{item.geojson_name}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{item.geometry_type}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.status === 'exact' ? 'bg-green-100 text-green-800' :
                        item.status === 'fuzzy' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={manualMatches[idx] || ''}
                        onChange={(e) => {
                          setManualMatches({
                            ...manualMatches,
                            [idx]: e.target.value
                          });
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="">Skip this item</option>
                        <option value="new">Create New Location</option>
                        {item.exact_match && (
                          <optgroup label="Exact Match">
                            <option value={item.geojson_name}>{item.geojson_name} (exact)</option>
                          </optgroup>
                        )}
                        {item.fuzzy_matches && item.fuzzy_matches.length > 0 && (
                          <optgroup label="Suggested Matches">
                            {item.fuzzy_matches.map((match) => (
                              <option key={match.id} value={match.id}>
                                {match.name} ({match.similarity}% match)
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {preview.all_locations && (
                          <optgroup label="All Locations">
                            {preview.all_locations
                              .filter(loc => 
                                // Don't show locations already in fuzzy matches
                                !item.fuzzy_matches?.some(fm => fm.id === loc.id)
                              )
                              .map((loc) => (
                                <option key={loc.id} value={loc.id}>
                                  {loc.name}
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={performImport}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Importing...' : 'Import Boundaries'}
          </button>
        </div>
      )}

      {/* Import Results */}
      {importResults && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Import Results</h2>
          
          <div className="space-y-4">
            {importResults.matched.length > 0 && (
              <div>
                <h3 className="font-semibold text-green-600">Matched & Updated ({importResults.matched.length})</h3>
                <ul className="text-sm mt-2 max-h-32 overflow-y-auto">
                  {importResults.matched.map((item, idx) => (
                    <li key={idx} className="py-1">
                      {item.name || item.geojson_name} 
                      {item.coordinates_count && (
                        <span className="text-gray-500 ml-2">({item.coordinates_count} points)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {importResults.created.length > 0 && (
              <div>
                <h3 className="font-semibold text-blue-600">Created New ({importResults.created.length})</h3>
                <ul className="text-sm mt-2 max-h-32 overflow-y-auto">
                  {importResults.created.map((item, idx) => (
                    <li key={idx} className="py-1">{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {importResults.skipped.length > 0 && (
              <div>
                <h3 className="font-semibold text-yellow-600">Skipped ({importResults.skipped.length})</h3>
                <ul className="text-sm mt-2 max-h-32 overflow-y-auto">
                  {importResults.skipped.map((item, idx) => (
                    <li key={idx} className="py-1">
                      {item.name && `${item.name}: `}{item.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {importResults.failed.length > 0 && (
              <div>
                <h3 className="font-semibold text-red-600">Failed ({importResults.failed.length})</h3>
                <ul className="text-sm mt-2 max-h-32 overflow-y-auto">
                  {importResults.failed.map((item, idx) => (
                    <li key={idx} className="py-1">
                      {item.name}: {item.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex gap-4">
            <a 
              href="/admin/location_boundaries"
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 inline-block"
            >
              Go to Boundary Editor
            </a>
            <button
              onClick={() => {
                setSelectedFile(null);
                setPreview(null);
                setImportResults(null);
                document.querySelector('input[type="file"]').value = '';
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Import Another File
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
    </div>
  );
};

window.GeojsonUploader = GeojsonUploader;

export default GeojsonUploader;